import { useEffect, useRef, useState } from "react";

const PTT_CHANNEL = "site-1";
const SAMPLE_RATE = 16000;
const PCM_CHUNK_SAMPLES = 512;
const PLAYBACK_PREBUFFER_CHUNKS = 3;
const PLAYBACK_SAFETY_DELAY_SEC = 0.03;
const PLAYBACK_RESET_GAP_SEC = 0.25;

function floatTo16BitPCM(float32Array) {
  const out = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i += 1) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function dispatchPttState(detail) {
  window.dispatchEvent(new CustomEvent("mint-ptt-state", { detail }));
}

function getWatchApiBase() {
  return `${window.location.origin}/watch-api`;
}

export default function WatchAlertSender() {
  const [message, setMessage] = useState("");
  const [pttConnected, setPttConnected] = useState(false);
  const [pttBusy, setPttBusy] = useState(false);
  const [pttSpeaking, setPttSpeaking] = useState(false);
  const [pttReceiving, setPttReceiving] = useState(false);
  const [pttError, setPttError] = useState("");
  const [pttLog, setPttLog] = useState("대기 중");
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [micDebug, setMicDebug] = useState("");

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const destroyedRef = useRef(false);

  const audioCtxRef = useRef(null);
  const muteGainRef = useRef(null);

  const playbackQueueRef = useRef([]);
  const playbackScheduledRef = useRef(false);
  const nextPlaybackTimeRef = useRef(0);
  const lastPlaybackScheduleAtRef = useRef(0);
  const receiveHoldTimerRef = useRef(null);

  const mediaStreamRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const processorRef = useRef(null);

  const startedRef = useRef(false);
  const pressingRef = useRef(false);

  const messageRef = useRef("");
  const textareaRef = useRef(null);

  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  const sendAlert = async () => {
    const currentValue =
      typeof textareaRef.current?.value === "string"
        ? textareaRef.current.value
        : messageRef.current;

    const trimmedMessage = String(currentValue || "").trim();

    if (!trimmedMessage) {
      alert("메시지를 입력하세요.");
      return;
    }

    const apiBase = getWatchApiBase();
    const payload = {
      siteId: PTT_CHANNEL,
      message: trimmedMessage,
    };

    try {
      console.log("[sendAlert] request url =", `${apiBase}/send-signal`);
      console.log("[sendAlert] payload =", payload);

      const res = await fetch(`${apiBase}/send-signal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const text = await res.text().catch(() => "");
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch (_) {
        data = null;
      }

      console.log("[sendAlert] status =", res.status);
      console.log("[sendAlert] response =", data || text);

      if (res.ok && (data?.ok === true || data?.success === true)) {
        alert("✅ 워치에 메시지 전송 완료!");
        setMessage("");
        messageRef.current = "";
        if (textareaRef.current) {
          textareaRef.current.value = "";
        }
        return;
      }

      const errorMessage =
        data?.error ||
        data?.message ||
        text ||
        `전송 실패 (${res.status})`;

      alert(`❌ ${errorMessage}`);
    } catch (err) {
      console.error("[sendAlert] network error:", err, payload);
      alert("❌ 서버 연결 실패");
    }
  };

  const resetPlaybackTimeline = () => {
    playbackQueueRef.current = [];
    playbackScheduledRef.current = false;
    nextPlaybackTimeRef.current = 0;
    lastPlaybackScheduleAtRef.current = 0;
  };

  const flushPlaybackQueue = async () => {
    playbackScheduledRef.current = false;

    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;

      if (ctx.state === "suspended") {
        await ctx.resume().catch(() => {});
      }

      if (playbackQueueRef.current.length === 0) return;

      const now = ctx.currentTime;

      if (
        nextPlaybackTimeRef.current === 0 ||
        now > nextPlaybackTimeRef.current + PLAYBACK_RESET_GAP_SEC
      ) {
        nextPlaybackTimeRef.current = now + PLAYBACK_SAFETY_DELAY_SEC;
      }

      while (playbackQueueRef.current.length > 0) {
        const chunk = playbackQueueRef.current.shift();
        if (!chunk || !chunk.length) continue;

        const floatData = new Float32Array(chunk.length);
        for (let i = 0; i < chunk.length; i += 1) {
          floatData[i] = chunk[i] / 32768;
        }

        const audioBuffer = ctx.createBuffer(1, floatData.length, SAMPLE_RATE);
        audioBuffer.copyToChannel(floatData, 0);

        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);

        const startAt = Math.max(
          nextPlaybackTimeRef.current,
          ctx.currentTime + 0.005
        );

        source.start(startAt);

        nextPlaybackTimeRef.current =
          startAt + audioBuffer.length / SAMPLE_RATE;
        lastPlaybackScheduleAtRef.current = ctx.currentTime;
      }
    } catch (err) {
      console.error("PTT playback error:", err);
      resetPlaybackTimeline();
    }
  };

  const schedulePlaybackFlush = () => {
    if (playbackScheduledRef.current) return;
    playbackScheduledRef.current = true;
    Promise.resolve().then(flushPlaybackQueue);
  };

  const pushPcmToPlayback = (arrayBuffer) => {
    try {
      const pcm16 = new Int16Array(arrayBuffer);
      if (!pcm16.length) return;

      playbackQueueRef.current.push(new Int16Array(pcm16));

      setPttReceiving(true);
      clearTimeout(receiveHoldTimerRef.current);
      receiveHoldTimerRef.current = setTimeout(() => {
        setPttReceiving(false);
      }, 800);

      const ctx = audioCtxRef.current;
      const now = ctx?.currentTime || 0;

      if (
        nextPlaybackTimeRef.current > 0 &&
        now > nextPlaybackTimeRef.current + PLAYBACK_RESET_GAP_SEC
      ) {
        resetPlaybackTimeline();
      }

      if (
        playbackQueueRef.current.length >= PLAYBACK_PREBUFFER_CHUNKS ||
        nextPlaybackTimeRef.current > 0
      ) {
        schedulePlaybackFlush();
      }
    } catch (err) {
      console.error("PCM playback queue error:", err);
    }
  };

  const ensureAudioContext = async () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) {
          throw new Error("이 브라우저는 Web Audio를 지원하지 않습니다.");
        }
        audioCtxRef.current = new Ctx({ sampleRate: SAMPLE_RATE });
      }

      if (!muteGainRef.current && audioCtxRef.current) {
        const muteGain = audioCtxRef.current.createGain();
        muteGain.gain.value = 0;
        muteGain.connect(audioCtxRef.current.destination);
        muteGainRef.current = muteGain;
      }

      if (audioCtxRef.current.state === "suspended") {
        await audioCtxRef.current.resume();
      }

      setAudioUnlocked(true);
      return audioCtxRef.current;
    } catch (err) {
      console.error(err);
      setPttError("브라우저 오디오 초기화 실패");
      throw err;
    }
  };

  const stopCapture = () => {
    try {
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current.onaudioprocess = null;
        processorRef.current = null;
      }
    } catch (_) {}

    try {
      if (mediaSourceRef.current) {
        mediaSourceRef.current.disconnect();
        mediaSourceRef.current = null;
      }
    } catch (_) {}

    try {
      if (mediaStreamRef.current) {
        for (const track of mediaStreamRef.current.getTracks()) {
          track.stop();
        }
        mediaStreamRef.current = null;
      }
    } catch (_) {}
  };

  const sendJson = (obj) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(obj));
    return true;
  };

  const stopPtt = () => {
    pressingRef.current = false;
    stopCapture();

    if (startedRef.current) {
      sendJson({ type: "stop", channel: PTT_CHANNEL, client: "web" });
    }

    startedRef.current = false;
    setPttSpeaking(false);
    setPttLog("송신 종료");

    dispatchPttState({
      connected: pttConnected,
      channel: PTT_CHANNEL,
      speaking: false,
      busy: pttBusy,
      error: "",
      lastType: "stop",
    });
  };

  const legacyGetUserMedia = (constraints) =>
    new Promise((resolve, reject) => {
      const nav = navigator;
      const getUserMediaFn =
        nav.getUserMedia ||
        nav.webkitGetUserMedia ||
        nav.mozGetUserMedia ||
        nav.msGetUserMedia;

      if (!getUserMediaFn) {
        reject(
          new Error("이 브라우저에서 마이크 접근 API를 사용할 수 없습니다.")
        );
        return;
      }

      getUserMediaFn.call(nav, constraints, resolve, reject);
    });

  const openMicrophone = async () => {
    const nav = navigator;

    let deviceLog = "";

    try {
      if (
        nav.mediaDevices &&
        typeof nav.mediaDevices.enumerateDevices === "function"
      ) {
        const devices = await nav.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((d) => d.kind === "audioinput");

        console.log("audio input devices:", audioInputs);

        deviceLog = audioInputs.length
          ? audioInputs
              .map((d, i) => `${i + 1}. ${d.label || "(이름 없음)"}`)
              .join(" / ")
          : "audioinput 없음";

        setMicDebug(deviceLog);
      } else {
        deviceLog = "enumerateDevices 미지원";
        setMicDebug(deviceLog);
      }
    } catch (err) {
      deviceLog = `장치목록 확인 실패: ${err?.message || "알 수 없는 오류"}`;
      setMicDebug(deviceLog);
    }

    const preferredConstraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    };

    let stream = null;

    try {
      if (
        nav.mediaDevices &&
        typeof nav.mediaDevices.getUserMedia === "function"
      ) {
        try {
          stream = await nav.mediaDevices.getUserMedia(preferredConstraints);
        } catch (firstErr) {
          console.warn("preferred getUserMedia failed:", firstErr);
          stream = await nav.mediaDevices.getUserMedia({ audio: true });
        }
      } else {
        stream = await legacyGetUserMedia({ audio: true });
      }
    } catch (err) {
      throw new Error(
        err?.message || "마이크 장치를 찾지 못했습니다. PC 입력장치를 확인하세요."
      );
    }

    const tracks = stream.getAudioTracks();
    if (!tracks || tracks.length === 0) {
      throw new Error("마이크 트랙을 찾지 못했습니다.");
    }

    console.log("mic track label:", tracks[0].label);
    setMicDebug((prev) =>
      `${prev}${prev ? " | " : ""}사용중: ${tracks[0].label || "기본 마이크"}`
    );

    return stream;
  };

  const startPtt = async () => {
    setPttError("");
    setMicDebug("");

    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setPttError("PTT 서버 연결 안됨");
      setPttLog("PTT 서버 연결 안됨");
      return;
    }

    if (pttBusy) {
      setPttLog("다른 장치가 사용 중");
      return;
    }

    try {
      await ensureAudioContext();

      const stream = await openMicrophone();
      mediaStreamRef.current = stream;

      const ctx = audioCtxRef.current;
      const source = ctx.createMediaStreamSource(stream);
      mediaSourceRef.current = source;

      const processor = ctx.createScriptProcessor(PCM_CHUNK_SAMPLES, 1, 1);
      processorRef.current = processor;

      const started = sendJson({
        type: "start",
        channel: PTT_CHANNEL,
        client: "web",
      });

      if (!started) {
        stopCapture();
        throw new Error("PTT start 전송 실패");
      }

      startedRef.current = true;
      setPttSpeaking(true);
      setPttBusy(false);
      setPttLog("송신 중");

      processor.onaudioprocess = (event) => {
        try {
          const wsNow = wsRef.current;
          if (!wsNow || wsNow.readyState !== WebSocket.OPEN) return;
          if (!startedRef.current) return;

          const input = event.inputBuffer.getChannelData(0);
          if (!input || !input.length) return;

          const pcm16 = floatTo16BitPCM(input);
          wsNow.send(pcm16.buffer);
        } catch (err) {
          console.error("PTT send audio error:", err);
        }
      };

      source.connect(processor);

      if (muteGainRef.current) {
        processor.connect(muteGainRef.current);
      } else {
        processor.connect(ctx.destination);
      }
    } catch (err) {
      console.error("PTT start error:", err);
      stopCapture();
      startedRef.current = false;
      setPttSpeaking(false);
      setPttLog("송신 시작 실패");

      const exact = `${err?.name || "Error"}: ${
        err?.message || "알 수 없는 오류"
      }`;
      setPttError(exact);
      setMicDebug((prev) => `${prev}${prev ? " | " : ""}오류: ${exact}`);
    }
  };

  const connectPtt = () => {
    if (destroyedRef.current) return;

    try {
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const wsUrl = `${protocol}://${window.location.host}/ptt`;

      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        if (destroyedRef.current) return;

        setPttConnected(true);
        setPttBusy(false);
        setPttError("");
        setPttLog("PTT 연결됨");

        sendJson({ type: "join", channel: PTT_CHANNEL, client: "web" });
      };

      ws.onmessage = async (event) => {
        if (typeof event.data !== "string") {
          pushPcmToPlayback(event.data);
          return;
        }

        let msg = null;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }

        const type = String(msg?.type || "");

        if (type === "hello") {
          setPttLog(`채널 연결됨 (${msg.channel || PTT_CHANNEL})`);
          return;
        }

        if (type === "joined") {
          setPttLog(`채널 입장 완료 (${msg.channel || PTT_CHANNEL})`);
          return;
        }

        if (type === "busy") {
          setPttBusy(true);
          setPttSpeaking(false);
          setPttLog("다른 장치가 사용 중");
          return;
        }

        if (type === "idle") {
          setPttBusy(false);
          setPttLog("대기 중");
          return;
        }

        if (type === "start_ok") {
          setPttBusy(false);
          setPttLog("송신 시작");
          return;
        }

        if (type === "ptt_start") {
          setPttBusy(true);
          setPttLog("워치 음성 수신 중");
          return;
        }

        if (type === "ptt_stop") {
          setPttBusy(false);
          setPttReceiving(false);
          setPttLog("대기 중");
          resetPlaybackTimeline();
          return;
        }

        if (type === "stopped") {
          stopCapture();
          startedRef.current = false;
          setPttSpeaking(false);
          setPttBusy(false);
          setPttLog("송신 종료");
          return;
        }
      };

      ws.onerror = (err) => {
        console.error("PTT websocket error:", err);
        setPttError("PTT 연결 오류");
        setPttLog("PTT 연결 오류");
      };

      ws.onclose = () => {
        setPttConnected(false);
        setPttBusy(false);
        setPttSpeaking(false);
        setPttReceiving(false);
        setPttLog("PTT 연결 종료");
        startedRef.current = false;
        stopCapture();
        resetPlaybackTimeline();

        if (!destroyedRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            connectPtt();
          }, 2000);
        }
      };
    } catch (err) {
      console.error(err);
      setPttError("PTT 초기화 실패");
      setPttLog("PTT 초기화 실패");
    }
  };

  useEffect(() => {
    destroyedRef.current = false;
    connectPtt();

    return () => {
      destroyedRef.current = true;

      clearTimeout(reconnectTimerRef.current);
      clearTimeout(receiveHoldTimerRef.current);

      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
        }
      } catch (_) {}

      wsRef.current = null;
      startedRef.current = false;
      pressingRef.current = false;

      stopCapture();
      resetPlaybackTimeline();

      try {
        if (muteGainRef.current) {
          muteGainRef.current.disconnect();
          muteGainRef.current = null;
        }
      } catch (_) {}

      try {
        if (audioCtxRef.current) {
          audioCtxRef.current.close();
        }
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    dispatchPttState({
      connected: pttConnected,
      channel: PTT_CHANNEL,
      speaking: pttSpeaking,
      busy: pttBusy,
      error: pttError,
      lastType: "sync",
    });
  }, [pttConnected, pttSpeaking, pttBusy, pttError]);

  const handlePressStart = async () => {
    if (pressingRef.current) return;
    pressingRef.current = true;

    try {
      await ensureAudioContext();
      await startPtt();
    } catch (_) {
      pressingRef.current = false;
    }
  };

  const handlePressEnd = () => {
    stopPtt();
  };

  return (
    <div className="flex h-full flex-col bg-transparent">
      <div className="mb-3 rounded-lg border border-gray-300 bg-gray-50 p-3 dark:border-gray-700 dark:bg-[#1B2432]">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              pttConnected
                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            }`}
          >
            {pttConnected ? "PTT 연결됨" : "PTT 연결 안됨"}
          </span>

          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            채널 {PTT_CHANNEL}
          </span>

          {pttSpeaking ? (
            <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300 animate-pulse">
              송신 중
            </span>
          ) : null}

          {!pttSpeaking && pttReceiving ? (
            <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300">
              수신 중
            </span>
          ) : null}

          {!pttSpeaking && !pttReceiving && pttBusy ? (
            <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
              다른 장치 사용 중
            </span>
          ) : null}

          {audioUnlocked ? (
            <span className="rounded-full bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
              오디오 준비됨
            </span>
          ) : null}
        </div>

        <div className="mb-2 text-xs text-gray-600 dark:text-gray-300">
          상태: {pttLog}
          {pttError ? (
            <span className="ml-2 text-red-500">({pttError})</span>
          ) : null}
        </div>

        {micDebug ? (
          <div className="mb-3 text-[11px] break-all text-gray-500 dark:text-gray-400">
            마이크 정보: {micDebug}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            className={`rounded-lg py-3 text-sm font-semibold text-white transition ${
              pttSpeaking
                ? "bg-red-600 hover:bg-red-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={() => {
              if (pttSpeaking || startedRef.current) handlePressEnd();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              handlePressStart();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              handlePressEnd();
            }}
            disabled={!pttConnected}
          >
            {pttSpeaking ? "눌러서 송신 중..." : "PTT 누르고 말하기"}
          </button>

          <button
            type="button"
            className="rounded-lg bg-slate-600 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
            onClick={async () => {
              try {
                await ensureAudioContext();
                setPttLog("오디오 활성화 완료");
              } catch (_) {}
            }}
          >
            스피커/오디오 활성화
          </button>
        </div>

        <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
          처음 한 번은 버튼을 눌러 오디오를 활성화한 뒤 테스트해라.
        </div>
      </div>

      <textarea
        ref={textareaRef}
        className="mb-2 min-h-[120px] flex-1 resize-none rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-[#1B2432] dark:text-white dark:placeholder:text-gray-400"
        placeholder="보낼 알림 내용을 입력하세요..."
        value={message}
        onChange={(e) => {
          setMessage(e.target.value);
          messageRef.current = e.target.value;
        }}
      />

      <button
        className="rounded-lg bg-blue-500 py-2 text-sm font-medium text-white transition hover:bg-blue-600"
        onClick={sendAlert}
      >
        워치에 알림 보내기
      </button>
    </div>
  );
}